import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { useDemo } from "@/contexts/DemoContext";
import { useAuth } from "@/hooks/useAuth";
import {
  mediaCaptureRepository,
  type ImportLegacyInput,
  type MediaEntityType,
  type ReviewItemInput,
  type SessionDecisionInput,
  type StartSessionInput,
  type UpdateItemInput,
  type UpdateSessionInput,
} from "@/repositories/mediaCapture";
import {
  storedUploadItemId,
  MediaUploadAbortedError,
  resumeKey,
  sessionResumeStore,
  startMediaUpload,
  type MediaUploadHandle,
  type UploadPlacement,
  type UploadProgress,
} from "@/lib/mediaUpload";

/**
 * Media-capture hooks — every call goes through `mediaCaptureRepository`
 * (Nest-only slice). Mutations invalidate the entity's workspace query, which
 * carries the checklist progress, items and legacy gallery together.
 */

export const mediaWorkspaceKey = (entityType: MediaEntityType, entityId: string | undefined) =>
  ["media-workspace", entityType, entityId] as const;

export function useMediaWorkspace(entityType: MediaEntityType, entityId: string | undefined) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [...mediaWorkspaceKey(entityType, entityId), companyId],
    queryFn: () => mediaCaptureRepository.getWorkspace(entityType, entityId!),
    enabled: !!companyId && !!entityId,
  });
}

function useInvalidateWorkspace(entityType: MediaEntityType, entityId: string | undefined) {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: mediaWorkspaceKey(entityType, entityId) }),
    [queryClient, entityType, entityId],
  );
}

export function useStartCaptureSession(entityType: MediaEntityType, entityId: string | undefined) {
  const invalidate = useInvalidateWorkspace(entityType, entityId);
  return useDemoSafeMutation({
    mutationFn: (input: StartSessionInput) => mediaCaptureRepository.startSession(input),
    onSuccess: invalidate,
  });
}

export function useUpdateCaptureSession(entityType: MediaEntityType, entityId: string | undefined) {
  const invalidate = useInvalidateWorkspace(entityType, entityId);
  return useDemoSafeMutation({
    mutationFn: ({ id, ...input }: UpdateSessionInput & { id: string }) =>
      mediaCaptureRepository.updateSession(id, input),
    onSuccess: invalidate,
  });
}

export function useUpdateMediaItem(entityType: MediaEntityType, entityId: string | undefined) {
  const invalidate = useInvalidateWorkspace(entityType, entityId);
  return useDemoSafeMutation({
    mutationFn: ({ id, ...input }: UpdateItemInput & { id: string }) =>
      mediaCaptureRepository.updateItem(id, input),
    onSuccess: invalidate,
  });
}

export function useImportLegacyMedia(entityType: MediaEntityType, entityId: string | undefined) {
  const invalidate = useInvalidateWorkspace(entityType, entityId);
  return useDemoSafeMutation({
    mutationFn: (input: ImportLegacyInput) => mediaCaptureRepository.importLegacy(input),
    onSuccess: invalidate,
  });
}

export function useDeleteMediaItem(entityType: MediaEntityType, entityId: string | undefined) {
  const invalidate = useInvalidateWorkspace(entityType, entityId);
  return useDemoSafeMutation({
    mutationFn: (id: string) => mediaCaptureRepository.deleteItem(id),
    onSuccess: invalidate,
  });
}

// ── Review workflow (PR 4b) ──────────────────────────────────────────────────

export const mediaReviewQueueKey = ["media-review-queue"] as const;
export const mediaSessionReviewKey = (sessionId: string | undefined) =>
  ["media-session-review", sessionId] as const;

/** Agent: submit the visit for review (warns about missing spaces — never blocks). */
export function useSubmitCaptureSession(entityType: MediaEntityType, entityId: string | undefined) {
  const invalidate = useInvalidateWorkspace(entityType, entityId);
  const queryClient = useQueryClient();
  return useDemoSafeMutation({
    mutationFn: (sessionId: string) => mediaCaptureRepository.submitSession(sessionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mediaReviewQueueKey });
      return invalidate();
    },
  });
}

/** Reviewer (`media.approve`): the company's in-review sessions, oldest first. */
export function useMediaReviewQueue(options: { enabled?: boolean } = {}) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [...mediaReviewQueueKey, companyId],
    queryFn: () => mediaCaptureRepository.getReviewQueue(),
    enabled: !!companyId && (options.enabled ?? true),
    staleTime: 30_000,
  });
}

/** Reviewer: one session with its items, checklist and missing required spaces. */
export function useMediaSessionReview(sessionId: string | undefined) {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [...mediaSessionReviewKey(sessionId), companyId],
    queryFn: () => mediaCaptureRepository.getSessionReview(sessionId!),
    enabled: !!companyId && !!sessionId,
  });
}

function useInvalidateReview(sessionId: string | undefined) {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: mediaSessionReviewKey(sessionId) }),
        queryClient.invalidateQueries({ queryKey: mediaReviewQueueKey }),
      ]),
    [queryClient, sessionId],
  );
}

/** Reviewer: approve / reject one in-review item (reject needs a reason). */
export function useReviewMediaItem(sessionId: string | undefined) {
  const invalidate = useInvalidateReview(sessionId);
  return useDemoSafeMutation({
    mutationFn: ({ id, ...input }: ReviewItemInput & { id: string }) =>
      mediaCaptureRepository.reviewItem(id, input),
    onSuccess: invalidate,
  });
}

/** Reviewer: approve all / request changes. Also refreshes the entity's Media tab. */
export function useDecideCaptureSession(sessionId: string | undefined) {
  const invalidate = useInvalidateReview(sessionId);
  const queryClient = useQueryClient();
  return useDemoSafeMutation({
    mutationFn: (input: SessionDecisionInput) => mediaCaptureRepository.decideSession(sessionId!, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["media-workspace"] });
      return invalidate();
    },
  });
}

/** Presigned GET of the private original — a READ fetched on demand (short-lived), so not demo-guarded. */
export function useMediaOriginalUrl() {
  return useMutation({
    mutationFn: (itemId: string) => mediaCaptureRepository.getOriginalUrl(itemId),
  });
}

/** Reviewer variant: works with only `media.approve` (no properties grant needed). */
export function useReviewOriginalUrl() {
  return useMutation({
    mutationFn: (itemId: string) => mediaCaptureRepository.getReviewOriginalUrl(itemId),
  });
}

// ── Upload queue ─────────────────────────────────────────────────────────────

export interface QueuedUpload {
  id: string;
  file: File;
  placement: UploadPlacement;
  progress: UploadProgress;
  error: string | null;
}

/**
 * Client-side ids for queued uploads. Module-level on purpose: the SPA has one
 * JS realm per tab, the ids only need to be unique within it (React keys and
 * queue lookups — never sent to the server or persisted), and sharing one
 * counter across hook instances keeps ids unique even with two workspaces open.
 */
let queueSeq = 0;

/**
 * Per-file upload state for the capture UI: start, retry (resumes from the
 * server's uploaded parts) and cancel (aborts server-side). Finished uploads
 * leave the list and refresh the workspace.
 */
export function useMediaUploadQueue(entityType: MediaEntityType, entityId: string | undefined) {
  const invalidate = useInvalidateWorkspace(entityType, entityId);
  const { isDemoAccount, showDemoModal } = useDemo();
  const [uploads, setUploads] = useState<QueuedUpload[]>([]);
  const handles = useRef(new Map<string, MediaUploadHandle>());
  const store = useRef(sessionResumeStore());

  const patch = useCallback((id: string, next: Partial<QueuedUpload>) => {
    setUploads((list) => list.map((u) => (u.id === id ? { ...u, ...next } : u)));
  }, []);

  const run = useCallback(
    (id: string, file: File, placement: UploadPlacement) => {
      const handle = startMediaUpload(file, placement, {
        transport: mediaCaptureRepository,
        store: store.current,
        onProgress: (progress) => patch(id, { progress }),
      });
      handles.current.set(id, handle);
      handle.promise.then(
        () => {
          handles.current.delete(id);
          setUploads((list) => list.filter((u) => u.id !== id));
          void invalidate();
        },
        (err: unknown) => {
          handles.current.delete(id);
          if (err instanceof MediaUploadAbortedError) {
            setUploads((list) => list.filter((u) => u.id !== id));
            void invalidate();
            return;
          }
          patch(id, { error: err instanceof Error ? err.message : String(err) });
        },
      );
    },
    [invalidate, patch],
  );

  const enqueue = useCallback(
    (files: File[], placement: UploadPlacement) => {
      // Same guard as every other write in the app (useDemoSafeMutation).
      if (isDemoAccount) {
        showDemoModal();
        return;
      }
      const added = files.map((file) => ({
        id: `upload-${++queueSeq}`,
        file,
        placement,
        error: null,
        progress: {
          phase: "preparing" as const,
          loaded: 0,
          total: file.size,
          percent: 0,
          itemId: null,
        },
      }));
      setUploads((list) => [...list, ...added]);
      added.forEach((u) => run(u.id, u.file, u.placement));
    },
    [run, isDemoAccount, showDemoModal],
  );

  const retry = useCallback(
    (id: string) => {
      const upload = uploads.find((u) => u.id === id);
      if (!upload) return;
      if (isDemoAccount) {
        showDemoModal();
        return;
      }
      patch(id, { error: null });
      run(id, upload.file, upload.placement);
    },
    [patch, run, uploads, isDemoAccount, showDemoModal],
  );

  const cancel = useCallback(
    async (id: string) => {
      const handle = handles.current.get(id);
      if (handle) {
        await handle.abort();
        return;
      }
      // A failed upload has no live handle: drop it server-side via a fresh abort.
      const upload = uploads.find((u) => u.id === id);
      const key = upload ? resumeKey(upload.file, upload.placement) : null;
      // A resume that failed before reading the server status has no itemId on
      // the queue entry — fall back to the stored one so the draft is aborted too.
      const itemId =
        upload?.progress.itemId ?? (key ? storedUploadItemId(store.current, key) : null);
      setUploads((list) => list.filter((u) => u.id !== id));
      // Always drop the resume entry, or the interrupted-uploads banner returns.
      if (key) store.current?.remove(key);
      if (itemId) {
        await mediaCaptureRepository.abortUpload(itemId).catch(() => undefined);
        void invalidate();
      }
    },
    [invalidate, uploads],
  );

  return { uploads, enqueue, retry, cancel };
}
